/**
 * 剪辑（P-D⑥/⑥b，蓝图 §4「云端工程列表 → 时间线编辑器」+ §6.2 导出剪映/FCPXML）。
 *
 * 自研轻量时间线，具备创作者操作：新建工程、**添加素材（资产库拉 / 本地上传）**、片段选中/拖拽移动+
 * **时间线/轨道吸附**、分割(剪切)、删除、**抽音轨(音画分离)**、**加字幕**、撤销/恢复、临时保存(本地草稿)、锁定、
 * **时间线缩放 + 自适应刻度**。真实合成/导出（剪映草稿/FCPXML）+ ffmpeg + ASR 待接后端。数据本地。
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Clapperboard, Download, Film, FolderInput, Lock, Magnet, Music, Play, Redo2, Save, Scissors, Sparkles, Trash2, Type, Undo2, Unlock, ZoomIn, ZoomOut } from 'lucide-react';

import {
  clipColor,
  loadDraft,
  moveClip,
  removeClip,
  saveDraft,
  snapTime,
  splitClip,
  tickStep,
  timelineDuration,
  toJianyingDraft,
  downloadJson,
  trackEnd,
  type Clip,
  type ClipKind,
  type EditProject,
  type Track,
} from '../../model/editing';
import { collectAssets } from '../../features/canvas/canvasLibrary';
import { useEditingStore } from '../editingStore';
import { Modal } from '../../ui/Modal';

const GUTTER = 72;
const SNAP_SEC = 0.15;
const KIND_NAME: Record<ClipKind, string> = { video: '画面', audio: '配音', subtitle: '字幕' };

function fmtDur(s: number): string {
  const m = Math.floor(s / 60);
  const ss = Math.round(s % 60);
  return `${m}:${ss.toString().padStart(2, '0')}`;
}
function newClipId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
}
function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.readAsDataURL(file);
  });
}

export function EditingPage() {
  const projects = useEditingStore((s) => s.projects);
  const addProject = useEditingStore((s) => s.addProject);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [playhead, setPlayhead] = useState(0);
  const [undoStack, setUndoStack] = useState<Track[][]>([]);
  const [redoStack, setRedoStack] = useState<Track[][]>([]);
  const [snapOn, setSnapOn] = useState(true);
  const [locked, setLocked] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [zoom, setZoom] = useState(64);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState('');
  const dragRef = useRef<{ id: string; trackId: string; startX: number; origStart: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const project = projects.find((p) => p.id === openId) ?? null;
  const total = timelineDuration(tracks);
  const assets = addOpen ? collectAssets().media : [];

  function flash(msg: string): void {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }

  function openProject(p: EditProject): void {
    const draft = loadDraft(p.id);
    setTracks(draft ?? p.tracks.map((t) => ({ ...t, clips: t.clips.map((c) => ({ ...c })) })));
    setOpenId(p.id);
    setSelected(null);
    setPlayhead(0);
    setUndoStack([]);
    setRedoStack([]);
    setDirty(false);
    setLocked(false);
  }
  function newProject(): void {
    const id = `ep-${Date.now().toString(36)}`;
    const p: EditProject = {
      id,
      name: `未命名工程 ${projects.length + 1}`,
      updatedAt: new Date().toISOString(),
      tracks: [
        { id: `${id}-v`, kind: 'video', name: '画面', clips: [] },
        { id: `${id}-a`, kind: 'audio', name: '配音', clips: [] },
        { id: `${id}-s`, kind: 'subtitle', name: '字幕', clips: [] },
      ],
    };
    addProject(p);
    openProject(p);
  }

  function commit(next: Track[]): void {
    setUndoStack((u) => [...u, tracks]);
    setRedoStack([]);
    setTracks(next);
    setDirty(true);
  }
  function undo(): void {
    const prev = undoStack.at(-1);
    if (prev === undefined) return;
    setUndoStack((u) => u.slice(0, -1));
    setRedoStack((r) => [...r, tracks]);
    setTracks(prev);
    setDirty(true);
  }
  function redo(): void {
    const nxt = redoStack.at(-1);
    if (nxt === undefined) return;
    setRedoStack((r) => r.slice(0, -1));
    setUndoStack((u) => [...u, tracks]);
    setTracks(nxt);
    setDirty(true);
  }

  function clipAt(id: string): { track: Track; clip: Clip } | null {
    for (const t of tracks) {
      const c = t.clips.find((x) => x.id === id);
      if (c !== undefined) return { track: t, clip: c };
    }
    return null;
  }

  /** 追加一个片段到某类轨道（默认轨尾）。 */
  function addClip(kind: ClipKind, name: string, dur: number, start?: number): void {
    if (locked) {
      flash('已锁定，先解除锁定');
      return;
    }
    const at = start ?? trackEnd(tracks, kind);
    const clip: Clip = { id: newClipId(), kind, name, start: at, dur: Math.max(1, dur) };
    const existing = tracks.find((t) => t.kind === kind);
    const next = existing
      ? tracks.map((t) => (t.id === existing.id ? { ...t, clips: [...t.clips, clip] } : t))
      : [...tracks, { id: `tr-${kind}-${Date.now().toString(36)}`, kind, name: KIND_NAME[kind], clips: [clip] }];
    commit(next);
  }
  function doSplit(): void {
    if (locked || selected === null) {
      flash(locked ? '已锁定' : '先选中一个片段');
      return;
    }
    const found = clipAt(selected);
    if (found === null) return;
    const next = tracks.map((t) => (t.id === found.track.id ? { ...t, clips: splitClip(t.clips, selected, playhead, newClipId()) } : t));
    commit(next);
    flash('已在播放头处分割');
  }
  function doDelete(): void {
    if (locked || selected === null) return;
    const found = clipAt(selected);
    if (found === null) return;
    commit(tracks.map((t) => (t.id === found.track.id ? { ...t, clips: removeClip(t.clips, selected) } : t)));
    setSelected(null);
  }
  function extractAudio(): void {
    const found = selected !== null ? clipAt(selected) : null;
    if (found === null || found.clip.kind !== 'video') {
      flash('先选中一个画面片段，再抽音轨');
      return;
    }
    addClip('audio', `${found.clip.name}·音轨`, found.clip.dur, found.clip.start);
    flash('已抽取音轨到配音轨（占位，接真后端解封装）');
  }
  function doSave(): void {
    if (openId === null) return;
    saveDraft(openId, tracks);
    setDirty(false);
    flash('已保存到本地草稿');
  }

  useEffect(() => {
    if (openId === null || !dirty) return;
    const h = setTimeout(() => saveDraft(openId, tracks), 800);
    return () => clearTimeout(h);
  }, [tracks, openId, dirty]);

  useEffect(() => {
    if (openId === null) return;
    const onKey = (e: KeyboardEvent): void => {
      const el = e.target as HTMLElement | null;
      if (el !== null && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected !== null) {
        e.preventDefault();
        doDelete();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function snapPoints(excludeId: string): number[] {
    const pts = [0, playhead];
    for (const t of tracks) for (const c of t.clips) {
      if (c.id === excludeId) continue;
      pts.push(c.start, c.start + c.dur);
    }
    return pts;
  }
  function onClipDown(e: React.PointerEvent, track: Track, clip: Clip): void {
    e.stopPropagation();
    setSelected(clip.id);
    if (locked) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setUndoStack((u) => [...u, tracks]);
    setRedoStack([]);
    dragRef.current = { id: clip.id, trackId: track.id, startX: e.clientX, origStart: clip.start };
  }
  function onClipMove(e: React.PointerEvent): void {
    const d = dragRef.current;
    if (d === null) return;
    const raw = d.origStart + (e.clientX - d.startX) / zoom;
    const t = snapOn ? snapTime(raw, snapPoints(d.id), SNAP_SEC) : Math.max(0, raw);
    setTracks((prev) => prev.map((tr) => (tr.id === d.trackId ? { ...tr, clips: moveClip(tr.clips, d.id, t) } : tr)));
    setDirty(true);
  }
  function onClipUp(): void {
    dragRef.current = null;
  }

  async function onUploadFiles(files: FileList | null): Promise<void> {
    if (files === null) return;
    for (const f of Array.from(files)) {
      const kind: ClipKind = f.type.startsWith('audio/') ? 'audio' : f.type.startsWith('image/') ? 'video' : 'video';
      const url = await readDataUrl(f);
      void url;
      addClip(kind, f.name.replace(/\.[^.]+$/, '').slice(0, 12), 5);
    }
    flash(`已导入 ${files.length} 个素材`);
  }

  // ---------- 工程列表 ----------
  if (project === null) {
    return (
      <div className="relative h-full overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col px-6 py-8">
          <header className="mb-5 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">剪辑</h1>
              <p className="mt-1 text-ui text-ink-muted">云端工程列表 → 时间线粗排/拼接/字幕/配音，导出剪映草稿 / FCPXML。</p>
            </div>
            <button type="button" onClick={newProject} className="btn-primary inline-flex items-center gap-1.5 px-3 py-2">
              <Clapperboard size={15} />
              新建工程
            </button>
          </header>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {projects.map((p) => (
              <button key={p.id} type="button" onClick={() => openProject(p)} className="panel group flex flex-col gap-2 p-4 text-left">
                <div className="flex h-28 items-center justify-center rounded-control border border-surface-line bg-gradient-to-br from-accent/20 to-surface-base">
                  <Film size={24} className="text-accent" />
                </div>
                <h3 className="truncate text-ui font-semibold">{p.name}</h3>
                <div className="flex items-center gap-2 text-uiSm text-ink-muted">
                  <span className="font-num">{fmtDur(timelineDuration(p.tracks))}</span>
                  <span className="text-ink-faint">·</span>
                  <span>{p.tracks.length} 轨</span>
                </div>
              </button>
            ))}
          </div>
          {toast !== '' && <Flash toast={toast} />}
        </div>
      </div>
    );
  }

  // ---------- 时间线编辑器 ----------
  const trackW = Math.max(total * zoom, 600);
  const step = tickStep(zoom);
  const tb = 'inline-flex items-center gap-1 rounded-control px-2 py-1 text-uiSm transition-colors disabled:opacity-40';
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-3 border-b border-surface-line px-4 py-2.5">
        <button type="button" onClick={() => setOpenId(null)} className="btn-ghost inline-flex items-center gap-1 px-2 py-1 text-uiSm">
          <ArrowLeft size={14} />
          工程列表
        </button>
        <h1 className="truncate text-ui font-semibold">{project.name}</h1>
        <span className="text-uiSm text-ink-faint">{fmtDur(total)}</span>
        {dirty && <span className="text-uiSm text-state-warn">● 未保存</span>}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setLocked((v) => !v)} className={`${tb} ${locked ? 'bg-accent/15 text-accent' : 'text-ink-secondary hover:text-ink-primary'}`}>
            {locked ? <Lock size={14} /> : <Unlock size={14} />}
            {locked ? '已锁定' : '未锁定'}
          </button>
          <button type="button" onClick={doSave} className={`${tb} text-ink-secondary hover:text-ink-primary`}>
            <Save size={14} />
            保存
          </button>
          <button type="button" onClick={() => { downloadJson(`${project.name}.jianying.json`, toJianyingDraft(project)); flash('已导出剪映草稿 JSON（本地下载）'); }} className={`${tb} text-ink-secondary hover:text-ink-primary`}>
            <Download size={14} />
            导出剪映
          </button>
          <button type="button" onClick={() => flash('导出 FCPXML：占位，接真后端生成')} className={`${tb} text-ink-secondary hover:text-ink-primary`}>
            <Download size={14} />
            FCPXML
          </button>
        </div>
      </header>

      {/* 预览 */}
      <div className="flex min-h-0 flex-1 items-center justify-center bg-black/40">
        <div className="flex aspect-video w-[min(70%,900px)] flex-col items-center justify-center gap-2 rounded-card border border-surface-line bg-surface-base text-ink-faint">
          <Play size={28} />
          <p className="text-uiSm">预览 · 播放头 {fmtDur(playhead)} / {fmtDur(total)}（接真后端渲染）</p>
        </div>
      </div>

      {/* 工具条 */}
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-t border-surface-line px-3 py-1.5">
        <button type="button" onClick={() => setAddOpen(true)} className={`${tb} bg-accent/15 font-semibold text-accent`}>
          <FolderInput size={15} />
          添加素材
        </button>
        <span className="mx-1 h-4 w-px bg-surface-line" />
        <button type="button" onClick={undo} disabled={undoStack.length === 0} className={`${tb} text-ink-secondary hover:text-ink-primary`} title="撤销 (Ctrl+Z)"><Undo2 size={15} /></button>
        <button type="button" onClick={redo} disabled={redoStack.length === 0} className={`${tb} text-ink-secondary hover:text-ink-primary`} title="恢复 (Ctrl+Shift+Z)"><Redo2 size={15} /></button>
        <span className="mx-1 h-4 w-px bg-surface-line" />
        <button type="button" onClick={doSplit} disabled={locked || selected === null} className={`${tb} text-ink-secondary hover:text-accent`} title="在播放头处分割"><Scissors size={15} />分割</button>
        <button type="button" onClick={doDelete} disabled={locked || selected === null} className={`${tb} text-ink-secondary hover:text-state-danger`} title="删除选中 (Delete)"><Trash2 size={15} />删除</button>
        <button type="button" onClick={extractAudio} disabled={locked || selected === null} className={`${tb} text-ink-secondary hover:text-accent`} title="从选中画面抽音轨"><Music size={15} />抽音轨</button>
        <button type="button" onClick={() => addClip('subtitle', '字幕', 3)} disabled={locked} className={`${tb} text-ink-secondary hover:text-accent`} title="在播放头加字幕"><Type size={15} />字幕</button>
        <button type="button" onClick={() => flash('AI 识别字幕：占位，接真后端 ASR')} className={`${tb} text-ink-secondary hover:text-accent`} title="语音识别生成字幕"><Sparkles size={15} />识别字幕</button>
        <span className="mx-1 h-4 w-px bg-surface-line" />
        <button type="button" onClick={() => setSnapOn((v) => !v)} className={`${tb} ${snapOn ? 'bg-accent/15 text-accent' : 'text-ink-secondary hover:text-ink-primary'}`} title="吸附"><Magnet size={15} />吸附</button>
        <span className="mx-1 h-4 w-px bg-surface-line" />
        <button type="button" onClick={() => setZoom((z) => Math.max(16, Math.round(z / 1.5)))} className={`${tb} text-ink-secondary hover:text-ink-primary`} title="缩小时间线"><ZoomOut size={15} /></button>
        <button type="button" onClick={() => setZoom((z) => Math.min(200, Math.round(z * 1.5)))} className={`${tb} text-ink-secondary hover:text-ink-primary`} title="放大时间线"><ZoomIn size={15} /></button>
      </div>

      {/* 时间线 */}
      <div className="h-60 shrink-0 overflow-x-auto overflow-y-hidden border-t border-surface-line bg-surface-raised/40">
        <div className="relative" style={{ width: trackW + GUTTER }} onClick={(e) => { const x = e.clientX - e.currentTarget.getBoundingClientRect().left - GUTTER; setPlayhead(Math.max(0, Math.min(total, x / zoom))); }}>
          <div className="pointer-events-none absolute top-0 bottom-0 z-20 w-px bg-accent" style={{ left: GUTTER + playhead * zoom }}>
            <span className="absolute -left-1 top-0 h-2 w-2 rounded-full bg-accent" />
          </div>
          {/* 自适应刻度 */}
          <div className="relative h-6 border-b border-surface-line" style={{ marginLeft: GUTTER, width: trackW }}>
            {Array.from({ length: Math.floor(total / step) + 1 }, (_, i) => i * step).map((s) => (
              <div key={s} className="absolute top-0 h-full border-l border-surface-line/60 pl-1 text-[10px] text-ink-faint" style={{ left: s * zoom }}>
                {fmtDur(s)}
              </div>
            ))}
          </div>
          {/* 轨道 */}
          {tracks.map((tr) => (
            <div key={tr.id} className="flex h-12 border-b border-surface-line/60">
              <div className="sticky left-0 z-10 flex w-[72px] shrink-0 items-center gap-1 border-r border-surface-line bg-surface-raised px-2 text-uiSm text-ink-secondary">
                {tr.kind === 'subtitle' ? <Type size={12} /> : tr.kind === 'audio' ? <Music size={12} /> : <Film size={12} />}
                <span className="truncate">{tr.name}</span>
              </div>
              <div className="relative flex-1">
                {tr.clips.map((c) => (
                  <div
                    key={c.id}
                    onPointerDown={(e) => onClipDown(e, tr, c)}
                    onPointerMove={onClipMove}
                    onPointerUp={onClipUp}
                    className={`clip-track ${clipColor(c.kind)} absolute top-1.5 bottom-1.5 flex items-center overflow-hidden rounded-control px-1.5 text-[11px] text-ink-primary ${locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} ${selected === c.id ? 'ring-2 ring-accent' : ''}`}
                    style={{ left: c.start * zoom, width: c.dur * zoom }}
                  >
                    <span className="truncate">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 添加素材弹层 */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="添加素材"
        summary="从资产库拉取，或本地上传；点击加入对应轨道（画面/配音）。"
        footer={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-ui"><FolderInput size={14} />本地上传</button>
            <button type="button" className="btn-primary px-3 py-1.5 text-ui" onClick={() => setAddOpen(false)}>完成</button>
          </div>
        }
      >
        <input ref={fileRef} type="file" accept="video/*,audio/*,image/*" multiple className="hidden" onChange={(e) => { void onUploadFiles(e.target.files); e.currentTarget.value = ''; }} />
        {assets.length === 0 ? (
          <p className="py-8 text-center text-uiSm text-ink-muted">资产库暂无素材。可「本地上传」，或先在画布/生成台产出素材。</p>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {assets.map((m) => (
              <button key={`${m.canvasId}-${m.nodeId}`} type="button" onClick={() => addClip(m.kind === 'audio' ? 'audio' : 'video', m.title, 5)} className="relative aspect-square overflow-hidden rounded-control border border-surface-line bg-surface-base transition-colors hover:border-accent">
                {m.thumbnail !== undefined || m.kind === 'image' ? <img src={m.thumbnail ?? m.url} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-ink-faint">{m.kind === 'audio' ? <Music size={18} /> : <Film size={18} />}</span>}
              </button>
            ))}
          </div>
        )}
      </Modal>

      {toast !== '' && <Flash toast={toast} />}
    </div>
  );
}

function Flash({ toast }: { toast: string }) {
  return (
    <div className="fixed bottom-6 left-1/2 z-50 max-w-lg -translate-x-1/2 rounded-card border border-accent/30 bg-surface-overlay px-4 py-2.5 text-uiSm text-ink-primary shadow-[0_6px_20px_rgba(0,0,0,.5)]">
      {toast}
    </div>
  );
}
